import { FC } from "react";
import { Loader2 } from "lucide-react";
import clsx from "clsx";

interface PageLoaderProps {
  /**
   * Loading message to display
   */
  message?: string;

  /**
   * Size of the loader
   */
  size?: "sm" | "md" | "lg";

  /**
   * Whether to show a backdrop
   */
  withBackdrop?: boolean;

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * PageLoader - Professioneller Page-Loader für ganze Seiten
 *
 * Features:
 * - Zentrierte Anzeige mit Spinner
 * - Anpassbare Nachricht
 * - Optionaler Backdrop
 * - Responsive Design
 * - Branding-konforme Farben
 */
export const PageLoader: FC<PageLoaderProps> = ({
  message = "Lade Daten...",
  size = "lg",
  withBackdrop = true,
  className,
}) => {
  const sizeStyles = {
    sm: {
      spinner: "w-6 h-6",
      text: "text-sm",
      container: "p-6",
    },
    md: {
      spinner: "w-8 h-8",
      text: "text-base",
      container: "p-8",
    },
    lg: {
      spinner: "w-12 h-12",
      text: "text-lg",
      container: "p-12",
    },
  };

  const currentSize = sizeStyles[size];

  const content = (
    <div
      className={clsx(
        "flex flex-col items-center justify-center space-y-4",
        currentSize.container,
        className,
      )}
    >
      {/* Spinner */}
      <Loader2 className={clsx("animate-spin text-primary-500", currentSize.spinner)} />

      {/* Message */}
      <div className={clsx("text-foreground font-medium text-center", currentSize.text)}>
        {message}
      </div>

      {/* Optional: Subtle animation dots */}
      <div className="flex space-x-1">
        <div className="w-1 h-1 bg-primary-500 rounded-full animate-pulse" />
        <div
          className="w-1 h-1 bg-primary-500 rounded-full animate-pulse"
          style={{ animationDelay: "0.2s" }}
        />
        <div
          className="w-1 h-1 bg-primary-500 rounded-full animate-pulse"
          style={{ animationDelay: "0.4s" }}
        />
      </div>
    </div>
  );

  if (withBackdrop) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
        <div className="bg-card border border-border rounded-lg shadow-xl">{content}</div>
      </div>
    );
  }

  return <div className="flex items-center justify-center min-h-[200px]">{content}</div>;
};

/**
 * InlineLoader - Kompakter Loader für Inline-Verwendung
 */
interface InlineLoaderProps {
  message?: string;
  size?: "sm" | "md";
  className?: string;
}

export const InlineLoader: FC<InlineLoaderProps> = ({
  message = "Lädt...",
  size = "sm",
  className,
}) => {
  const sizeStyles = {
    sm: {
      spinner: "w-4 h-4",
      text: "text-xs",
      container: "p-3",
    },
    md: {
      spinner: "w-5 h-5",
      text: "text-sm",
      container: "p-4",
    },
  };

  const currentSize = sizeStyles[size];

  return (
    <div
      className={clsx(
        "flex items-center space-x-2 text-muted-foreground",
        currentSize.container,
        className,
      )}
    >
      <Loader2 className={clsx("animate-spin text-primary-500", currentSize.spinner)} />
      <span className={currentSize.text}>{message}</span>
    </div>
  );
};

/**
 * CardLoader - Loader für Card-Komponenten
 */
interface CardLoaderProps {
  message?: string;
  className?: string;
}

export const CardLoader: FC<CardLoaderProps> = ({ message = "Lade...", className }) => {
  return (
    <div
      className={clsx(
        "flex flex-col items-center justify-center space-y-3 p-8",
        "bg-background border border-border rounded-lg",
        className,
      )}
    >
      <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
      <span className="text-sm text-foreground font-medium">{message}</span>
    </div>
  );
};
